import { buildUrlWithParams } from 'src/lib/utils';
import type { DepartmentParams } from './DepartmentService';
import type { ShiftGeneratorTaskParams } from './ShiftGeneratorTaskService';
import type { OrgEmployeeRequestsParams } from './OrgEmployeeRequestService';

export const DEPARTMENT_PATH = '/api/v1/department';
export const ORG_PATH = '/api/v1/org';

// TODO: Use hooks where the new query logic allows it
export const PATHS = {
    organizations: '/api/v1/orgs',
    organization: (orgId: string): string => `${ORG_PATH}/${orgId}`,
    orgLicense: (orgId: string): string => `${ORG_PATH}/${orgId}/org-license`,
    departmentWithLicense: (orgId: string): string =>
        `${ORG_PATH}/${orgId}/department-with-license`,
    orgUsers: (orgId: string): string => `${ORG_PATH}/${orgId}/org-users`,
    orgUserCreate: (orgId: string): string => `${ORG_PATH}/${orgId}/org-user`,
    orgUser: (orgId: string, userId: string): string =>
        `${ORG_PATH}/${orgId}/org-user/${userId}`,
    countryRules: '/api/v1/countryrules',
    userExternalOrganizations: '/api/v1/user/external-org',
    orgDepartments: (orgId: string): string =>
        `${ORG_PATH}/${orgId}/departments`,
    departmentEmployeeRequestsMultiple: (departmentId: string): string =>
        `${DEPARTMENT_PATH}/${departmentId}/employee/requests/multiple`,
    departmentEmployeeRequest: (
        departmentId: string,
        employeeId: string,
        requestId: string,
    ): string =>
        `${DEPARTMENT_PATH}/${departmentId}/employee/${employeeId}/request/${requestId}`,
    departmentEmployeeRequestStatus: (
        departmentId: string,
        employeeId: string,
        requestId: string,
    ): string =>
        `${DEPARTMENT_PATH}/${departmentId}/employee/${employeeId}/request/${requestId}/status`,
    departmentEmployeeRequestComment: (
        departmentId: string,
        employeeId: string,
        requestId: string,
    ): string =>
        `${DEPARTMENT_PATH}/${departmentId}/employee/${employeeId}/request/${requestId}/comment`,
    department: (departmentId: string, params?: DepartmentParams): string =>
        buildUrlWithParams(`${DEPARTMENT_PATH}/${departmentId}`, params),
    departmentActiveLicense: (departmentId: string): string =>
        `${DEPARTMENT_PATH}/${departmentId}/active-license`,
    departmentUnshareMe: (departmentId: string): string =>
        `${DEPARTMENT_PATH}/${departmentId}/unshare-me`,
    shiftTypes: (departmentId: string): string =>
        `${DEPARTMENT_PATH}/${departmentId}/shift-types`,
    positions: (departmentId: string): string =>
        `${DEPARTMENT_PATH}/${departmentId}/positions`,
    traits: (departmentId: string): string =>
        `${DEPARTMENT_PATH}/${departmentId}/traits`,
    shiftTypeCreate: (departmentId: string): string =>
        `${DEPARTMENT_PATH}/${departmentId}/shift-type`,
    shiftType: (departmentId: string, shiftTypeId: string): string =>
        `${DEPARTMENT_PATH}/${departmentId}/shift-type/${shiftTypeId}`,
    tasks: (departmentId: string): string =>
        `${DEPARTMENT_PATH}/${departmentId}/tasks`,
    taskCreate: (departmentId: string): string =>
        `${DEPARTMENT_PATH}/${departmentId}/task`,
    runnerExecute: (orgId: string): string =>
        `${ORG_PATH}/${orgId}/runner/new/execute`,
    task: (
        departmentId: string,
        taskId: string,
        params?: ShiftGeneratorTaskParams,
    ): string =>
        buildUrlWithParams(
            `${DEPARTMENT_PATH}/${departmentId}/task/${taskId}`,
            params,
        ),
    result: (departmentId: string, taskId: string): string =>
        `${DEPARTMENT_PATH}/${departmentId}/task/${taskId}/result`,
    costPlan: (departmentId: string, taskId: string): string =>
        `${DEPARTMENT_PATH}/${departmentId}/task/${taskId}/result/cost-plan`,
    resultBreaches: (
        departmentId: string,
        taskId: string,
        rules: string[],
    ): string =>
        buildUrlWithParams(
            `${DEPARTMENT_PATH}/${departmentId}/task/${taskId}/result/breaches`,
            { rules },
        ),
    employeesCriticalWorkMinutes: (
        departmentId: string,
        taskId: string,
    ): string =>
        `${DEPARTMENT_PATH}/${departmentId}/task/${taskId}/result/employees-critical-work-minutes`,
    employee: (departmentId: string, employeeId: string): string =>
        `${DEPARTMENT_PATH}/${departmentId}/employee/${employeeId}`,
    employeeCreate: (departmentId: string): string =>
        `${DEPARTMENT_PATH}/${departmentId}/employee`,
    employeeExtId: (departmentId: string, employeeId: string): string =>
        `${DEPARTMENT_PATH}/${departmentId}/employee/${employeeId}/ext-id`,
    employeePattern: (
        departmentId: string,
        employeeId: string,
        patternCase: string,
    ): string =>
        buildUrlWithParams(
            `${DEPARTMENT_PATH}/${departmentId}/employee/${employeeId}/pattern`,
            { type: patternCase },
        ),
    employeePatternById: (
        departmentId: string,
        employeeId: string,
        patternId: string,
        patternCase: string,
    ): string =>
        buildUrlWithParams(
            `${DEPARTMENT_PATH}/${departmentId}/employee/${employeeId}/pattern/${patternId}`,
            { type: patternCase },
        ),
    employees: (departmentId: string): string =>
        `${DEPARTMENT_PATH}/${departmentId}/employees`,
    logo: (orgId: string): string => `${ORG_PATH}/${orgId}/logo`,
    employeeFinances: (departmentId: string): string =>
        `${DEPARTMENT_PATH}/${departmentId}/employee-finance/wages`,
    employeeFinance: (departmentId: string, employeeId: string): string =>
        `${DEPARTMENT_PATH}/${departmentId}/employee-finance/${employeeId}/wage`,
    employeeWage: (
        departmentId: string,
        employeeId: string,
        wageId: string,
    ): string =>
        `${DEPARTMENT_PATH}/${departmentId}/employee-finance/${employeeId}/wage/${wageId}`,
    patternTemplate: (departmentId: string): string =>
        `${DEPARTMENT_PATH}/${departmentId}/pattern-template`,
    patternTemplateById: (departmentId: string, templateId: string): string =>
        `${DEPARTMENT_PATH}/${departmentId}/pattern-template/${templateId}`,
    patternTemplates: (departmentId: string): string =>
        `${DEPARTMENT_PATH}/${departmentId}/pattern-templates`,
    orgEmployees: (orgId: string): string =>
        `${ORG_PATH}/${orgId}/org-employee/all`,
    orgEmployeeEmployee: (
        orgId: string,
        orgEmployeeId: string,
        employeeId: string,
    ): string =>
        `${ORG_PATH}/${orgId}/org-employee/${orgEmployeeId}/employee/${employeeId}`,
    orgEmployeeRequest: (
        orgId: string,
        orgEmployeeId: string,
        requestId: string,
    ): string =>
        `${ORG_PATH}/${orgId}/org-employee/${orgEmployeeId}/request/${requestId}`,
    orgEmployeeRequestPattern: (
        orgId: string,
        orgEmployeeId: string,
        requestId: string,
    ): string =>
        `${ORG_PATH}/${orgId}/org-employee/${orgEmployeeId}/request/${requestId}`,
    orgEmployeeRequestStatus: (
        orgId: string,
        orgEmployeeId: string,
        requestId: string,
    ): string =>
        `${ORG_PATH}/${orgId}/org-employee/${orgEmployeeId}/request/${requestId}/status`,
    orgEmployeeRequestsMultiple: (orgId: string): string =>
        `${ORG_PATH}/${orgId}/org-employee/requests/multiple`,
    orgEmployeeRequests: (
        orgId: string,
        orgEmployeeId: string,
        params: OrgEmployeeRequestsParams,
    ): string =>
        buildUrlWithParams(
            `${ORG_PATH}/${orgId}/org-employee/${orgEmployeeId}/requests`,
            params,
        ),
    orgEmployeeRequestComment: (
        orgId: string,
        orgEmployeeId: string,
        requestId: string,
    ): string =>
        `${ORG_PATH}/${orgId}/org-employee/${orgEmployeeId}/request/${requestId}/comment`,
};
